import React, { useEffect, useState } from "react";

import { colors } from "@zapalacorp/tokens";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import * as yup from "yup";

import Breadcrumb from "../../../../../../components/Generic/Breadcrump";
import SubHeader from "../../../../../../components/Generic/SubHeader";
import UITabMaterial, { PanelProps } from "../../../../../../components/Generic/UITabMaterial";
import { PRIVATE_ROUTES } from "../../../../../../constants";
import { AccountProvider } from "../../../../../../context/AccountContext";
import { useAccountHook } from "../../../../../../hooks/useAccountHook";
import { NetworkTabs } from "../../utils";
import { useActions } from "../hooks/useActions";
import { handleValidate } from "./actions";
import Details from "./Details";
import SkeletonAccountDetails from "./Details/Skeleton";
import useStyles, { MainDiv } from "./styles";
import { useAppState } from "Context/StateContext";
import { checkResourceCan, FN_NAMES, hasPermission, isShowAction } from "Util/permissions";
import { ResourceCanEnum, ResourceName } from "Util/enums/accessRule";
import { isMoving } from "Util/validation";

interface HistoryState {
  accountId?: number;
}

const AccountDetails = () => {
  const [errors, setErrors] = useState<yup.ValidationError[]>([]);
  const [isSubmitted, setIsSubmitted] = useState<boolean>();

  const { account, handleUpdateAccount } = useAccountHook();

  const { t: translate } = useTranslation();

  const classes = useStyles();

  const history = useHistory<HistoryState>();

  const locationState = history.location?.state;

  const {
    accountId,
    setAccountId,
    getAccountById,
    responseError,
    useCreateAccount,
    useUpdateAccount,
  } = useActions();
  const { currentUser, userPermissions } = useAppState();
  const [accountPermission, setAccountPermission] = useState<{
    edit: boolean;
    create: boolean;
  }>({
    edit: false,
    create: false,
  });

  useEffect(() => {
    if (userPermissions && userPermissions?.length > 0) {
      setAccountPermission({
        edit:
          checkResourceCan(userPermissions, ResourceName.ACCOUNTS, ResourceCanEnum.EDIT) &&
          hasPermission(userPermissions, FN_NAMES.FUNCTION_NETWORK_ACCOUNTS_EDIT),
        create:
          checkResourceCan(userPermissions, ResourceName.ACCOUNTS, ResourceCanEnum.EDIT) &&
          hasPermission(userPermissions, FN_NAMES.FUNCTION_NETWORK_ACCOUNTS_ADD_NEW),
      });
    }
  }, [userPermissions]);

  useEffect(() => {
    setAccountId(locationState?.accountId || account?.id);
  }, [locationState, account]);

  const { isFetching } = getAccountById();

  useEffect(() => {
    if (isSubmitted) {
      handleValidate(translate, account, setErrors);
    }
  }, [account]);

  const { mutate: createAccount, isLoading: isCreatingAccount } = useCreateAccount(() => goBack());

  const { mutate: updateAccount, isLoading: isUpdatingAccount } = useUpdateAccount(() => goBack());

  const onSave = () => {
    setIsSubmitted(true);

    if (handleValidate(translate, account, setErrors)) {
      submit();
    }
  };

  const goBack = () => {
    history.replace(`${PRIVATE_ROUTES.NETWORK}?tab=${NetworkTabs.ACCOUNTS}`);
  };

  const submit = () => {
    if (account && account?.id) {
      updateAccount({ id: account.id, account });
    } else {
      createAccount(account);
    }
  };

  const detailsPanel: PanelProps = {
    key: "details",
    menuItem: translate("details"),
    className: classes.uiTab,
    loading: isFetching,
    skeleton: () => <SkeletonAccountDetails />,
    render: () => {
      if (
        (!accountId && !isShowAction(currentUser, accountPermission?.create)) ||
        (accountId && !isShowAction(currentUser, accountPermission?.edit))
      )
        return null;

      return (
        <Details
          account={account}
          errors={errors}
          handleUpdateAccount={handleUpdateAccount}
          onSave={onSave}
          onCancel={goBack}
          responseError={responseError}
          loadingAccount={isCreatingAccount || isUpdatingAccount}
        />
      );
    },
  };

  const accountPanels = [detailsPanel];

  return (
    <div>
      {!isMoving(currentUser?.company) && (
        <SubHeader>
          <Breadcrumb
            items={[
              {
                title: translate("network"),
                link: {
                  url: `${PRIVATE_ROUTES.NETWORK}?tab=accounts`,
                },
              },
              {
                title:
                  account && account?.id
                    ? `${translate("edit")} ${account?.name || ""}`
                    : translate("newAccount"),
                color: colors.gray600,
              },
            ]}
          />
        </SubHeader>
      )}
      <MainDiv>
        <UITabMaterial leftPanels={accountPanels} />
      </MainDiv>
    </div>
  );
};

const AccountMaintenance = () => {
  return (
    <AccountProvider>
      <AccountDetails />
    </AccountProvider>
  );
};

export default AccountMaintenance;
